import React, { useState, useEffect, useRef } from 'react';
import { LocationFormModalProps } from '../../types/location';
import styles from './CategoryFormModal.module.css';
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { fromLonLat, toLonLat } from 'ol/proj';
import { Style, Icon } from 'ol/style';

const LocationFormModal: React.FC<LocationFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  isLoading = false,
}) => {
  const [name, setName] = useState('');
  const [latitude, setLatitude] = useState<string | number>('');
  const [longitude, setLongitude] = useState<string | number>('');

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<Map | null>(null);
  const markerFeatureRef = useRef<Feature | null>(null);
  const vectorSourceRef = useRef<VectorSource | null>(null);

  useEffect(() => {
    if (initialData) {
      setName(initialData.location_name || '');
      if (initialData.long_lat) {
        const coords = initialData.long_lat.split(',');
        if (coords.length === 2) {
          setLongitude(parseFloat(coords[0].trim()));
          setLatitude(parseFloat(coords[1].trim()));
        } else {
          setLongitude('');
          setLatitude('');
        }
      } else {
        setLongitude('');
        setLatitude('');
      }
    } else {
      setName('');
      setLongitude('');
      setLatitude('');
    }
  }, [initialData, isOpen]);

  useEffect(() => {
    if (!isOpen || !mapRef.current) return;
    
    const mapElement = mapRef.current;
    let destroyed = false;
    vectorSourceRef.current = new VectorSource();
    const vectorLayer = new VectorLayer({
      source: vectorSourceRef.current,
      style: new Style({
        image: new Icon({ anchor: [0.5, 1], src: 'https://openlayers.org/en/latest/examples/data/icon.png' })
      })
    });

    let initialCenter = fromLonLat([0, 0]);
    let initialZoom = 2;
    
    if (longitude !== '' && latitude !== '') {
      initialCenter = fromLonLat([Number(longitude), Number(latitude)]);
      initialZoom = 13;
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (destroyed || !mapInstanceRef.current) return;
          const userLocation = fromLonLat([position.coords.longitude, position.coords.latitude]);
          mapInstanceRef.current.getView().setCenter(userLocation);
          mapInstanceRef.current.getView().setZoom(13);
        },
        () => {}
      );
    }

    mapInstanceRef.current = new Map({
      target: mapElement,
      layers: [
        new TileLayer({ source: new OSM() }),
        vectorLayer,
      ],
      view: new View({ center: initialCenter, zoom: initialZoom }),
    });

    if (longitude !== '' && latitude !== '') {
      markerFeatureRef.current = new Feature({ geometry: new Point(initialCenter) });
      vectorSourceRef.current.addFeature(markerFeatureRef.current);
    }

    mapInstanceRef.current.on('click', (event) => {
      if (!mapInstanceRef.current) return;
      const coords = toLonLat(event.coordinate);
      setLongitude(parseFloat(coords[0].toFixed(6)));
      setLatitude(parseFloat(coords[1].toFixed(6)));
    });

    return () => {
      destroyed = true;
      const currentMap = mapInstanceRef.current;
      if (currentMap) {
        currentMap.setTarget(undefined);
        mapInstanceRef.current = null;
      }
      if (mapElement) mapElement.innerHTML = '';
      markerFeatureRef.current = null;
      vectorSourceRef.current = null;
    };
  }, [isOpen, latitude, longitude]);

  useEffect(() => {
    if (
      latitude !== '' && longitude !== '' &&
      mapInstanceRef.current && vectorSourceRef.current
    ) {
      const newCoords = fromLonLat([Number(longitude), Number(latitude)]);
      if (!markerFeatureRef.current) {
        markerFeatureRef.current = new Feature({ geometry: new Point(newCoords) });
        vectorSourceRef.current.addFeature(markerFeatureRef.current);
      } else {
        const geom = markerFeatureRef.current.getGeometry();
        if (geom && geom instanceof Point) geom.setCoordinates(newCoords);
      }
      mapInstanceRef.current.getView().setCenter(newCoords);
    }
  }, [latitude, longitude]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    let combinedLongLat: string | null = null;
    if (latitude !== '' && longitude !== '') {
      combinedLongLat = `${latitude},${longitude}`;
    }

    if (initialData) {
      onSubmit({ 
        location_id: initialData.id,
        location_name: name, 
        long_lat: combinedLongLat
      });
    } else {
      onSubmit({ 
        location_name: name, 
        long_lat: combinedLongLat
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={`${styles.modalContent} ${styles.modalContentWide}`} onClick={(e) => e.stopPropagation()}>
        <h3>{initialData ? 'Edit Location' : 'Add New Location'}</h3>
        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="locationName">Location Name *</label>
            <input
              type="text"
              id="locationName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>

          <div className={styles.formGroup}>
            <label>Location on Map (Click to select)</label>
            <div ref={mapRef} className={styles.mapContainer}></div>
          </div>

          <div className={styles.coordinateRow}>
            <div className={styles.coordinateField}>
              <label htmlFor="longitude">Longitude</label>
              <input
                type="number"
                step="any"
                id="longitude"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value === '' ? '' : parseFloat(e.target.value))}
                placeholder="e.g., 35.123456"
                disabled={isLoading}
                className={styles.coordinateInput}
              />
            </div>
            <div className={styles.coordinateField}>
              <label htmlFor="latitude">Latitude</label>
              <input
                type="number"
                step="any"
                id="latitude"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value === '' ? '' : parseFloat(e.target.value))}
                placeholder="e.g., 31.654321"
                disabled={isLoading}
                className={styles.coordinateInput}
              />
            </div>
          </div>

          <div className={styles.formActions}>
            <button type="submit" className={styles.submitButton} disabled={isLoading}>
              {isLoading ? 'Saving...' : (initialData ? 'Save Changes' : 'Add Location')}
            </button>
            <button type="button" onClick={onClose} className={styles.cancelButton} disabled={isLoading}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LocationFormModal;
